// Top performer utilities — derive ranked player lists from raw stats.
//
// These are pure functions (no React dependencies) so they're trivially testable.
// Used by the dashboard formatter to build stat card data (top-3 per metric).
//
// Uses the same string accessor + cast pattern as stat-utils.ts to access
// fields on the PlayerSummaryStats union type.
import type {
  PlayerStatsWithId,
  ReportingPlayer,
  TopPerformanceEntry
} from '@frontend/core/lib/domains/reporting/types'
import { getStatValue } from '@frontend/core/lib/domains/reporting/types'

/**
 * Sum a metric across all players' stats.
 */
export function sumMetric(
  playerStats: PlayerStatsWithId[],
  metricKey: string
): number {
  return playerStats.reduce((sum, ps) => {
    if (!ps.stats) return sum
    const raw = (ps.stats as Record<string, unknown>)[metricKey]
    const value = getStatValue(raw as Parameters<typeof getStatValue>[0])
    return sum + (value ?? 0)
  }, 0)
}

/**
 * Derive top-3 performers for a given metric from player stats.
 * Joins playerStats with players by ID to get display names.
 */
export function deriveTop3(
  playerStats: PlayerStatsWithId[],
  players: ReportingPlayer[],
  metricKey: string
): TopPerformanceEntry[] {
  const playerMap = new Map(players.map((p) => [p.id, p]))

  return playerStats
    .map((ps) => {
      if (!ps.stats) return null
      const raw = (ps.stats as Record<string, unknown>)[metricKey]
      const value = getStatValue(raw as Parameters<typeof getStatValue>[0])
      const player = playerMap.get(ps.playerId)
      if (value == null || !player) return null
      return { playerValue: value, player }
    })
    .filter((entry): entry is TopPerformanceEntry => entry !== null)
    .sort((a, b) => b.playerValue - a.playerValue)
    .slice(0, 3)
}
